/* eslint-disable @typescript-eslint/no-explicit-any */
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Icon from '@mui/material/Icon';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React from 'react';

import { getError } from '@/utils/hookFormUtils';

import type { TooltipProps } from '@mui/material/Tooltip';

interface InputFieldProps {
  label?: React.ReactNode;
  name: string;
  type?: string;
  register?: any;
  errors?: any;
  onChange?: any;
  textFieldProps?: any;
  variant?: 'filled' | 'outlined' | 'standard';
  sxInput?: any;
  sxLabel?: any;
  disabled?: boolean;
  tooltipText?: string;
  tooltipLink?: string;
  icon?: React.ReactNode;
  placeholder?: string;
  value?: string;
}

const CssTextField = styled(TextField)(({ theme }) => ({
  '& label.Mui-focused': {
    color: theme.palette.primary.main,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: theme.palette.primary.main,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  type = 'text',
  register,
  errors,
  onChange,
  textFieldProps,
  variant = 'outlined',
  sxInput,
  sxLabel,
  disabled,
  tooltipText,
  tooltipLink,
  icon,
  placeholder,
  value,
}) => {
  const theme = useTheme();
  const error = getError(errors, name);

  return (
    <Stack width="100%">
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '0rem', margin: '1rem 0.5rem 0.5rem 0' }}
      >
        {icon && (
          <Icon color="disabled" sx={{ marginRight: '0.5rem' }}>
            {icon}
          </Icon>
        )}

        {label && (
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.grey[500],
              fontWeight: 'bold',
              // margin: '1rem 0.5rem 0.5rem 0',
              ...sxLabel,
            }}
          >
            {label}
          </Typography>
        )}

        {tooltipText && (
          <HtmlTooltip
            title={
              <Typography variant="body2">
                {tooltipText}{' '}
                {tooltipLink && (
                  <a href={tooltipLink} target="__blank">
                    Ver más
                  </a>
                )}
              </Typography>
            }
          >
            <HelpOutlineIcon color="primary" />
          </HtmlTooltip>
        )}
      </Box>

      <CssTextField
        {...textFieldProps}
        {...register?.(name)}
        {...(onChange && { onChange })}
        value={value ?? register?.(name)?.value}
        placeholder={placeholder ?? (typeof label === 'string' ? label : '')}
        type={type}
        sx={{ width: '100%', ...sxInput }}
        error={!!error}
        helperText={error?.message}
        variant={variant}
        disabled={disabled}
      />
    </Stack>
  );
};

export default InputField;
